import { format } from 'date-fns';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import { useAdminUsers } from '@/api/hooks';

const PROVIDER_LABELS: Record<string, string> = {
  credential: 'Email',
  microsoft:  'Microsoft 365',
};

export default function AdminUsersPage() {
  const { data, isLoading, error, refetch, isFetching } = useAdminUsers();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Users</h1>
          <p className="text-xs text-muted-foreground">
            {data ? `${data.users.length} registered account${data.users.length !== 1 ? 's' : ''}` : ''}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load users: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {data && (
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">User</th>
                <th className="px-4 py-2.5">Autotask ID</th>
                <th className="px-4 py-2.5">Sign-in</th>
                <th className="px-4 py-2.5">Registered</th>
                <th className="px-4 py-2.5">Last login</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {data.users.map((u) => (
                <tr key={u.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <UserAvatar name={u.name || u.email} size={28} />
                      <div className="min-w-0">
                        <div className="truncate font-medium">{u.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {u.autotaskResourceId ?? <span className="italic">none</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {/* a user can hold two accounts for one provider (SSO re-linked after the Postgres move) */}
                      {[...new Set(u.providers)].map((p) => (
                        <span
                          key={p}
                          className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                        >
                          {PROVIDER_LABELS[p] ?? p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {u.createdAt ? format(new Date(u.createdAt), 'PP') : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {u.lastLogin ? format(new Date(u.lastLogin), 'PPp') : <span className="italic">never</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
